import * as THREE from 'three';

export interface InteractiveObjectValidation {
  id: string;
  type: string;
  isDetectable: boolean;
  hierarchyPath: string[];
  issues: string[];
}

export interface ValidationResult {
  isValid: boolean;
  totalObjects: number;
  interactiveObjects: InteractiveObjectValidation[];
  hierarchyIssues: string[];
  recommendations: string[];
}

/**
 * Scene Hierarchy Validator
 * Prevents interaction issues by validating object hierarchy and detectability
 */
export class SceneHierarchyValidator {
  private scene: THREE.Scene;
  private selectionManager: any; // Type would be SelectionManager but avoiding circular imports

  constructor(scene: THREE.Scene, selectionManager?: any) {
    this.scene = scene;
    this.selectionManager = selectionManager;
  }

  /**
   * Validate all interactive objects in the scene hierarchy
   */
  public validateInteractiveObjects(): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      totalObjects: 0,
      interactiveObjects: [],
      hierarchyIssues: [],
      recommendations: []
    };

    // Traverse scene and collect object information
    this.scene.traverse((object) => {
      result.totalObjects++;

      if (this.isInteractiveObject(object)) {
        const validation = this.validateObject(object);
        result.interactiveObjects.push(validation);

        if (!validation.isDetectable) {
          result.isValid = false;
          result.hierarchyIssues.push(
            `Object ${validation.id} (${validation.type}) is not detectable via raycasting`
          );
        }

        if (validation.issues.length > 0) {
          result.isValid = false;
          result.hierarchyIssues.push(...validation.issues);
        }
      }
    });

    // Generate recommendations
    result.recommendations = this.generateRecommendations(result);

    return result;
  }

  /**
   * Validate a specific interactive object
   */
  private validateObject(object: THREE.Object3D): InteractiveObjectValidation {
    const validation: InteractiveObjectValidation = {
      id: object.userData['id'] || 'unknown',
      type: object.userData['type'] || 'unknown',
      isDetectable: false,
      hierarchyPath: this.getHierarchyPath(object),
      issues: []
    };

    // Check if object is a mesh (required for raycasting)
    if (!(object instanceof THREE.Mesh)) {
      validation.issues.push('Object is not a THREE.Mesh - cannot be raycast');
    }

    // Check if object has required userData
    if (!object.userData['id']) {
      validation.issues.push('Missing userData.id');
    }

    if (!object.userData['type']) {
      validation.issues.push('Missing userData.type');
    }

    // Check if object would be detected by SelectionManager
    validation.isDetectable = this.wouldBeDetectedByRaycaster(object);

    // Check for common hierarchy issues
    this.checkCommonHierarchyIssues(object, validation);

    return validation;
  }

  /**
   * Check if object would be detected by the raycaster
   */
  private wouldBeDetectedByRaycaster(object: THREE.Object3D): boolean {
    // Simulate the SelectionManager.isInteractive() logic
    if (!(object instanceof THREE.Mesh)) return false;

    const userData = object.userData;
    if (!userData['id']) return false;

    // Exclude background objects
    if (userData['type'] === 'background' || userData['type'] === 'environment') {
      return false;
    }

    // Include known interactive types
    return userData['type'] === 'ship' || 
           userData['type'] === 'planet' || 
           userData['type'] === 'block' ||
           userData['type'] === 'mining-collector' ||
           userData['interactive'] === true;
  }

  /**
   * Check if object qualifies as interactive
   */
  private isInteractiveObject(object: THREE.Object3D): boolean {
    const userData = object.userData;
    return !!(userData['id'] && (userData['type'] || userData['interactive']));
  }

  /**
   * Get the full hierarchy path from scene root to object
   */
  private getHierarchyPath(object: THREE.Object3D): string[] {
    const path: string[] = [];
    let current: THREE.Object3D | null = object;

    while (current && current !== this.scene) {
      const name = current.name || current.constructor.name || 'Unknown';
      const type = current.userData['type'] || '';
      path.unshift(type ? `${name}(${type})` : name);
      current = current.parent;
    }

    path.unshift('Scene');
    return path;
  }

  /**
   * Check for common hierarchy issues that cause interaction problems
   */
  private checkCommonHierarchyIssues(object: THREE.Object3D, validation: InteractiveObjectValidation): void {
    // Check if object is nested too deeply in groups
    const pathDepth = validation.hierarchyPath.length;
    if (pathDepth > 6) {
      validation.issues.push(`Object is nested ${pathDepth} levels deep - may impact performance`);
    }

    // Check if object is in a group that might not be traversed
    const groupParents = validation.hierarchyPath.filter(name => 
      name.includes('Group') || name.includes('Manager')
    );

    if (groupParents.length > 2) {
      validation.issues.push(
        `Object is nested in multiple groups: ${groupParents.join(' -> ')} - verify raycasting traversal`
      );
    }

    // Check for detached groups (common cause of interaction issues)
    let current = object.parent;
    let foundSceneConnection = false;
    while (current) {
      if (current === this.scene) {
        foundSceneConnection = true;
        break;
      }
      current = current.parent;
    }

    if (!foundSceneConnection) {
      validation.issues.push('Object is not connected to the main scene hierarchy');
    }

    // Check for visibility issues
    if (!object.visible) {
      validation.issues.push('Object is not visible');
    }

    // Check parent visibility
    current = object.parent;
    while (current && current !== this.scene) {
      if (!current.visible) {
        validation.issues.push(`Parent object ${current.name || 'unknown'} is not visible`);
        break;
      }
      current = current.parent;
    }
  }

  /**
   * Generate recommendations based on validation results
   */
  private generateRecommendations(result: ValidationResult): string[] {
    const recommendations: string[] = [];

    if (result.hierarchyIssues.length > 0) {
      recommendations.push(
        'Ensure all interactive objects are properly added to the scene hierarchy'
      );

      const hasHierarchyIssues = result.hierarchyIssues.some(issue => 
        issue.includes('not connected to the main scene')
      );

      if (hasHierarchyIssues) {
        recommendations.push(
          'Add objects to parent groups that are already in the scene, rather than directly to scene'
        );
      }

      const hasMeshIssues = result.hierarchyIssues.some(issue => 
        issue.includes('not a THREE.Mesh')
      );

      if (hasMeshIssues) {
        recommendations.push(
          'Ensure interactive objects are THREE.Mesh instances with proper geometry and materials'
        );
      }

      const hasUserDataIssues = result.hierarchyIssues.some(issue => 
        issue.includes('Missing userData')
      );

      if (hasUserDataIssues) {
        recommendations.push(
          'Set userData.id and userData.type for all interactive objects'
        );
      }
    }

    if (result.interactiveObjects.length === 0) {
      recommendations.push(
        'No interactive objects found - verify objects have proper userData configuration'
      );
    }

    return recommendations;
  }

  /**
   * Test specific object interactivity
   */
  public testObjectInteractivity(objectId: string): InteractiveObjectValidation | null {
    let foundObject: THREE.Object3D | null = null;

    this.scene.traverse((object) => {
      if (object.userData['id'] === objectId) {
        foundObject = object;
      }
    });

    if (!foundObject) {
      return null;
    }

    return this.validateObject(foundObject);
  }

  /**
   * Get a summary report for debugging
   */
  public getDebugReport(): string {
    const result = this.validateInteractiveObjects();
    
    let report = `=== Scene Hierarchy Validation Report ===\n`;
    report += `Total Objects: ${result.totalObjects}\n`;
    report += `Interactive Objects: ${result.interactiveObjects.length}\n`;
    report += `Validation Status: ${result.isValid ? 'PASS' : 'FAIL'}\n\n`;

    if (result.interactiveObjects.length > 0) {
      report += `Interactive Objects:\n`;
      result.interactiveObjects.forEach(obj => {
        report += `  • ${obj.id} (${obj.type}): ${obj.isDetectable ? 'DETECTABLE' : 'NOT DETECTABLE'}\n`;
        report += `    Path: ${obj.hierarchyPath.join(' -> ')}\n`;
        if (obj.issues.length > 0) {
          report += `    Issues: ${obj.issues.join(', ')}\n`;
        }
      });
      report += '\n';
    }

    if (result.hierarchyIssues.length > 0) {
      report += `Issues:\n`;
      result.hierarchyIssues.forEach(issue => {
        report += `  ⚠️  ${issue}\n`;
      });
      report += '\n';
    }

    if (result.recommendations.length > 0) {
      report += `Recommendations:\n`;
      result.recommendations.forEach(rec => {
        report += `  💡 ${rec}\n`;
      });
    }

    return report;
  }
}

/**
 * Utility function to validate scene and log results
 */
export function validateSceneHierarchy(scene: THREE.Scene, logResults = true): ValidationResult {
  const validator = new SceneHierarchyValidator(scene);
  const result = validator.validateInteractiveObjects();

  if (logResults) {
    console.log(validator.getDebugReport());
  }

  return result;
}